import { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, Link } from 'react-router-dom'
import { signupSchema, SignupFormValues } from './schemas'
import { useAuth } from '@/context/auth-context'

function SignupForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [signupComplete, setSignupComplete] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()

  // Use Auth Context instead of Redux
  const { signUp } = useAuth()

  // Use an effect to handle navigation after signup is complete
  useEffect(() => {
    if (signupComplete) {
      console.log('Navigation effect triggered, redirecting to email-verification')

      // Use a timeout to ensure state updates before navigation
      const navigationTimer = setTimeout(() => {
        // Force a hard redirect to the email verification page
        window.location.replace('/email-verification')
      }, 500)

      return () => clearTimeout(navigationTimer)
    }
  }, [signupComplete])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      username: '',
    },
  })

  const onSubmit = async (data: SignupFormValues) => {
    try {
      console.log('Form submission started', data.email, data.username)

      // Update button state manually
      if (buttonRef.current) {
        buttonRef.current.disabled = true
        buttonRef.current.textContent = 'Creating account...'
      }

      setIsLoading(true)
      setError(null)

      // Use Auth Context for signup
      const { error: authError } = await signUp({
        email: data.email,
        password: data.password,
        username: data.username,
      })

      if (authError) {
        console.error('Signup error:', authError)
        setError(authError)

        // Reset button state
        if (buttonRef.current) {
          buttonRef.current.disabled = false
          buttonRef.current.textContent = 'Create account'
        }

        setIsLoading(false)
        return
      }

      console.log('Signup flow complete, setting signupComplete to trigger navigation')

      // Set signup complete to trigger the navigation effect
      setSignupComplete(true)

      // Try immediate navigation as a fallback
      try {
        console.log('Attempting immediate navigation with React Router')
        navigate('/email-verification', { replace: true })
      } catch (navError) {
        console.error('React Router navigation error:', navError)

        // Fallback to direct location change if navigate fails
        console.log('Falling back to direct location change')
        window.location.href = '/email-verification'
      }
    } catch (err) {
      console.error('Unexpected error during signup:', err)
      setError(err instanceof Error ? err.message : 'Failed to sign up')

      // Reset button state
      if (buttonRef.current) {
        buttonRef.current.disabled = false
        buttonRef.current.textContent = 'Create account'
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-background rounded-xl shadow-lg">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text">Create an Account</h1>
        <p className="text-text/70 mt-2">Join Boardgames Today to connect with fellow gamers</p>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-text">
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="you@example.com"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('email')}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="username" className="text-sm font-medium text-text">
            Username
          </label>
          <input
            id="username"
            type="text"
            placeholder="boardgamer123"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('username')}
          />
          {errors.username && (
            <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-text">
            Password
          </label>
          <input
            id="password"
            type="password"
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('password')}
          />
          {errors.password && (
            <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="text-sm font-medium text-text">
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        <button
          ref={buttonRef}
          type="submit"
          disabled={isLoading}
          className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Creating account...' : 'Create account'}
        </button>
      </form>

      <div className="text-center text-sm text-text/70">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Sign in
        </Link>
      </div>
    </div>
  )
}

export { SignupForm }
